import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { InvestorTask } from '../../data/types';
import { date, daysBetween, DEMO_TODAY } from '../../lib/format';
import { TASK_LABELS } from '../../lib/labels';
import { useInvestorScope } from '../../store/selectors';
import { Card, shortEntityName } from '../common';
import TaskActionDialog from './TaskActionDialog';

export default function TaskList({ tasks, compact }: { tasks: InvestorTask[]; compact?: boolean }) {
  const scope = useInvestorScope();
  const navigate = useNavigate();
  const [active, setActive] = useState<InvestorTask | null>(null);

  const act = (task: InvestorTask) => {
    if (task.kind === 'PAY_CALL') navigate('/investor/payments');
    else if (task.kind === 'COMPLETE_APPLICATION' || task.kind === 'SIGN_DOCUMENT')
      navigate(`/investor/apply/${task.refId ?? 'new'}`);
    else setActive(task);
  };

  if (tasks.length === 0) {
    return (
      <Card>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CheckCircleOutlineIcon color="success" />
          <Typography variant="body2">You&apos;re all up to date.</Typography>
        </Stack>
      </Card>
    );
  }
  return (
    <>
      <Stack spacing={1.25}>
        {tasks.map((task) => {
          const holder = scope.holders.find((item) => item.id === task.holderId);
          const days = task.dueDate ? daysBetween(DEMO_TODAY, task.dueDate) : undefined;
          return (
            <Card
              key={task.id}
              sx={{
                borderLeft: task.status === 'OPEN' ? '4px solid #b26a00' : undefined,
                opacity: task.status === 'DONE' ? 0.7 : 1,
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ sm: 'center' }}
              >
                <AssignmentLateOutlinedIcon
                  sx={{
                    color: task.status === 'OPEN' ? '#b26a00' : 'success.main',
                    display: { xs: 'none', sm: 'block' },
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack
                    useFlexGap
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ flexWrap: 'wrap', rowGap: 0.5 }}
                  >
                    <Typography sx={{ fontWeight: 700 }}>{task.title}</Typography>
                    <Chip
                      size="small"
                      label={TASK_LABELS[task.kind]}
                      sx={{ height: 20, fontSize: '0.68rem' }}
                    />
                  </Stack>
                  {!compact && (
                    <Typography variant="body2" color="text.secondary">
                      {task.detail}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {holder ? shortEntityName(holder) : ''}
                    {task.dueDate
                      ? ` · due ${date(task.dueDate)}${days !== undefined && task.status === 'OPEN' ? (days >= 0 ? ` (${days} days)` : ` (overdue by ${-days} days)`) : ''}`
                      : ''}
                  </Typography>
                </Box>
                {task.status === 'OPEN' ? (
                  <Button variant="contained" onClick={() => act(task)} sx={{ flexShrink: 0 }}>
                    {task.kind === 'PAY_CALL'
                      ? 'Pay now'
                      : task.kind === 'QUOTE_TFN'
                        ? 'Add TFN'
                        : task.kind === 'RENEW_WHOLESALE'
                          ? 'Upload'
                          : task.kind === 'REFRESH_KYC'
                            ? 'Verify'
                            : task.kind === 'CONFIRM_BANK'
                              ? 'Confirm'
                              : 'Continue'}
                  </Button>
                ) : (
                  <Chip color="success" variant="outlined" label="Done" />
                )}
              </Stack>
            </Card>
          );
        })}
      </Stack>
      <TaskActionDialog task={active} onClose={() => setActive(null)} />
    </>
  );
}
